import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import get from 'lodash.get';
import {
  Box,
  Button,
  Checkbox,
  FlexBox,
  Grid,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Typography
} from '@kis-technologies/kis-ui';
import BarcodeScan from 'assets/barcode_scan.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { Scrollbar } from 'components/Scrollbar';
import { UNKNOWN, OPERATIONAL } from 'constants/hardware';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, actions as cartActions } from 'services/cart';
import { selectors as configSelectors, Configuration, LanguageField, Messages } from 'services/configuration';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner } from 'services/hardware';
import { selectors as kioskSelectors, KioskHardware } from 'services/kiosk';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { formatCurrency } from 'utils/stringFormat';
import { ManualEntry } from './components/ManualEntry';
import { DEFAULT_TEXT } from './strings';
import { useStyles } from './styled';
import { Upsell, TicketDetails, ProductSettings } from './types';

const SouvenirTicketView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Actions and Selectors
  const { reserveTickets, resetCartError } = useActions(cartActions);
  const { scannerRead, resetScanner } = useActions(hardwareActions);
  const { getEventDetails, resetEventDetails } = useActions(ticketingActions);

  const cart = useSelector(cartSelectors.selectCart);
  const cartError = useSelector(cartSelectors.selectError);
  const cartReserving = useSelector(cartSelectors.selectReserving);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const { status, loading: scannerLoading }: Scanner = useSelector(hardwareSelectors.selectScanner);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const eventDetails = useSelector(ticketingSelectors.selectEventDetails);
  const ticketingLoading = useSelector(ticketingSelectors.selectLoading);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const isPreviewMode = sessionStorage.getItem('mode') === 'preview';
  const languageSettings: LanguageField[] = get(configuration, 'configuration.languageSettings', []);
  const souvenirTicketEntry = get(configuration, `${ticketingPath}.souvenirTicket.ticketEntry`);
  const messages: Messages = get(souvenirTicketEntry, 'messages', {});
  const headerText = get(messages, `header.${i18n.language}`, DEFAULT_TEXT.ticketEntryHeader);
  const subheaderText = get(messages, `subheader.${i18n.language}`, DEFAULT_TEXT.ticketEntrySubheader);
  const backButtonText = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButtonText = get(messages, `buttons.${i18n.language}[1]`, t('commonText.continue'));
  const manualEntryButtonText = get(messages, `buttons.${i18n.language}[2]`, DEFAULT_TEXT.manualEntryButton);
  const isManualEntryEnabled: boolean = get(souvenirTicketEntry, 'manualEntry.enabled', false);
  const productSettingsList: ProductSettings[] = get(souvenirTicketEntry, 'productSettingsList', []);
  const support = get(configuration, `${ticketingPath}.supportName.${i18n.language}`) || t('commonText.support');

  const [pageInitialized, setPageInitialized] = useState(false);
  const [uiLoading, setUILoading] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showGoBackWarning, setShowGoBackWarning] = useState(false);
  const [selectedUpsells, setSelectedUpsells] = useState<number[]>([]);
  const [mockTickets, setMockTickets] = useState<TicketDetails[]>([]);
  const [mockSection] = useState(() => Math.floor(Math.random() * 255) + 1);
  const [mockRow] = useState(() => Math.floor(Math.random() * 255) + 1);
  const [mockSeat, setMockSeat] = useState(() => Math.floor(Math.random() * 255) + 1);
  const [keyboardValue, setKeyboardValue] = useState<string>('');

  const classes = useStyles();

  const productSettings = React.useMemo(() => {
    if (!eventDetails) return null;
    return productSettingsList.find(
      (p: any) => p.name?.trim().toLowerCase() === eventDetails.name?.trim().toLowerCase()
    );
  }, [productSettingsList, eventDetails]);

  const upsells: Upsell[] = (productSettings?.upsells || []).map((u: any, i: number) => ({
    id: i + 1,
    label: u.label,
    price: u.price
  }));

  const ticketTotal = mockTickets.reduce((sum, ti) => sum + ti.price, 0);
  const upsellTotal =
    mockTickets.length * upsells.filter((u) => selectedUpsells.includes(u.id)).reduce((sum, u) => sum + u.price, 0);
  const grandTotal = ticketTotal + upsellTotal;
  const selectedUpsellPrice = upsells
    .filter((u) => selectedUpsells.includes(u.id))
    .reduce((sum, u) => sum + u.price, 0);

  useEffect(() => {
    if (!eventDetails) getEventDetails();
    resetScanner();
  }, []);

  useEffect(() => {
    if (eventDetails) setPageInitialized(true);
  }, [eventDetails]);

  useEffect(() => {
    if (pageInitialized) startScannerIfApplicable();
  }, [pageInitialized]);

  // Event listener for NFC reader acting as keyboard wedge
  useEffect(() => {
    if (!showManualEntry) {
      document.addEventListener('keydown', handleKeydown, true);
    } else {
      document.removeEventListener('keydown', handleKeydown, true);
    }

    return () => {
      document.removeEventListener('keydown', handleKeydown, true);
    };
  }, [showManualEntry]);

  // Add mock ticket when keyboard value changes
  useEffect(() => {
    if (keyboardValue.length > 0) {
      addMockTicket();
      setKeyboardValue('');
    }
  }, [keyboardValue]);

  useEffect(() => {
    if (!pageInitialized) return;

    // scanner successfully read something
    if (status.message && status.code === OPERATIONAL) {
      addMockTicket();
      resetScanner();
      startScannerIfApplicable();
    } else if (status.rawCode === 'scanner_timeout') {
      resetScanner();
      startScannerIfApplicable();
    }
  }, [status.message, status.rawCode]);

  useEffect(() => {
    if (cart && !cartReserving && !cartError) {
      navigate('/ticketing/cart');
    } else if (cartError) {
      setUILoading(false);
    }
  }, [cartReserving, cart, cartError]);

  // Handle input from keyboard or NFC reader
  const handleKeydown = (event: any) => {
    const value = event.key;
    if (value === 'Enter') {
      setKeyboardValue((prev) => prev + value);
    }
  };

  const addMockTicket = () => {
    if (!eventDetails || !eventDetails.offers?.length) return;

    const basePrice = eventDetails.offers[0].basePrice;

    const ticket = {
      section: mockSection,
      row: mockRow,
      seat: mockSeat,
      price: basePrice
    };

    setMockTickets((prev) => [...prev, ticket]);
    setMockSeat((prev) => prev + 1);
  };

  const removeTicket = (index: number) => {
    setMockTickets((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleUpsell = (id: number) => {
    setSelectedUpsells((prev) => (prev.includes(id) ? prev.filter((existingId) => existingId !== id) : [...prev, id]));
  };

  const handleTicketSubmit = () => {
    addMockTicket();
    resetScanner();
  };

  /**
   * Start reading input from the scanner if this criteria is met:
   * - Page has been initialized
   * - Not in preview mode
   * - Scanner status is not UNKNOWN (i.e. scanner is connected)
   * - Scanner is not already loading
   */
  const startScannerIfApplicable = () => {
    if (pageInitialized && !isPreviewMode && status && status.code !== UNKNOWN && !scannerLoading) {
      scannerRead();
    }
  };

  const getBarcodeScanImgUrl = () => {
    if (kiosk) {
      const barcodeScanner = kiosk.hardware.find((device: KioskHardware) => device.type === 'barcodeScanner');
      if (barcodeScanner && barcodeScanner.imageUrl && barcodeScanner.imageUrl.primary) {
        return barcodeScanner.imageUrl.primary;
      }
    }
    return undefined;
  };

  const onNextPageClick = () => {
    if (!eventDetails) return;

    const offer = eventDetails.offers[0];
    const quantity = mockTickets.length;

    if (!offer || quantity === 0) return;

    const reservePayload = Array.from({ length: quantity }, () => ({
      ...offer.purchaseParams
    }));

    setUILoading(true);
    reserveTickets(reservePayload);
  };

  const onPrevPageClick = () => {
    resetEventDetails();
    navigate('/ticketing/events', { state: { from: location.pathname } });
  };

  const Row = (rowProps: { row: TicketDetails; idx: number }) => {
    const { row, idx } = rowProps;

    return (
      <MuiTable.TableRow className={classes.tableRow}>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.ticketDetailsTableBodyCell}`}>
          <Typography fontSize={24} fontWeight={600}>
            {`Section ${row.section}  Row ${row.row}  Seat ${row.seat}`}
          </Typography>
        </MuiTable.TableCell>
        <MuiTable.TableCell
          className={`${classes.tableBodyCell} ${classes.ticketPriceTableBodyCell} ${classes.numberTableBodyCell}`}
        >
          <Typography fontSize={24} fontWeight={600}>
            {formatCurrency(row.price + selectedUpsellPrice, {}, languageSettings)}
          </Typography>
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.removeTableBodyCell}`}>
          <FlexBox align="center" justify="center">
            <SvgIconButton
              onClick={() => removeTicket(idx)}
              appearance="transparent"
              iconColor="#FFF"
              background="rgb(224, 32, 32)"
              data-testid={'exit-icon-button'}
            >
              <SvgIcon name="exit" size={40} />
            </SvgIconButton>
          </FlexBox>
        </MuiTable.TableCell>
      </MuiTable.TableRow>
    );
  };

  if (showManualEntry) {
    return (
      <ManualEntry
        loading={ticketingLoading}
        value={''}
        onCancel={() => {
          setShowManualEntry(false);
          startScannerIfApplicable();
        }}
        onSubmit={() => {
          setShowManualEntry(false);
          handleTicketSubmit();
        }}
      />
    );
  }

  if (ticketingLoading || cartReserving || uiLoading) {
    return <LoadingOverlay style={{ zIndex: '99' }} />;
  }

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid container direction="column" className={classes.innerContainer}>
          <MuiBox className={classes.headingContainer}>
            <Typography variant="h2" fontSize={52} sx={{ lineHeight: 1.2 }}>
              {headerText}
            </Typography>
            <Typography fontSize={24}>{subheaderText}</Typography>
          </MuiBox>
          <MuiBox className={classes.contentContainer}>
            <MuiBox className={classes.imageAndUpsellsContainer}>
              <FlexBox flexOverride="1 0 auto" align="center" justify="center">
                {!getBarcodeScanImgUrl() && (
                  <MuiBox className={classes.imageBox}>
                    <img height={500} alt="url" src={BarcodeScan} />
                  </MuiBox>
                )}
                {getBarcodeScanImgUrl() && <img width={450} height={500} alt="url" src={getBarcodeScanImgUrl()} />}
              </FlexBox>
              {mockTickets.length > 0 &&
                upsells.map((u) => (
                  <MuiBox key={u.id} mt={2} className={classes.upsellContainer} onClick={() => toggleUpsell(u.id)}>
                    <Checkbox
                      size="medium"
                      checked={selectedUpsells.includes(u.id)}
                      label={
                        <Typography fontSize={24} fontWeight={600}>
                          {`${u.label} - ${formatCurrency(u.price, {}, languageSettings)}`}
                        </Typography>
                      }
                    />
                  </MuiBox>
                ))}
            </MuiBox>
            {mockTickets.length > 0 && (
              <MuiBox className={classes.ticketsTableAndTotalContainer}>
                <MuiBox className={classes.ticketsTableContainer}>
                  <Scrollbar refreshModel={mockTickets}>
                    <Box flexOverride="none" padding="0 3" fullHeight>
                      <MuiTable.TableContainer id="souvenirTicketTable">
                        <MuiTable.Table className={classes.table} stickyHeader sx={{ borderCollapse: 'collapse' }}>
                          <MuiTable.TableBody>
                            {mockTickets.map((ticket, idx) => (
                              <Row idx={idx} row={ticket} />
                            ))}
                          </MuiTable.TableBody>
                        </MuiTable.Table>
                      </MuiTable.TableContainer>
                    </Box>
                  </Scrollbar>
                </MuiBox>
                {isManualEntryEnabled && (
                  <MuiBox className={classes.portraitManualEntryButtonContainer}>
                    {/* Component will display only in portrait mode*/}
                    <MuiBox className={classes.portraitManualEntryButtonBox}>
                      <Button
                        onClick={() => setShowManualEntry(true)}
                        appearance="white"
                        customSize="x-large"
                        data-testid="type-code-button"
                      >
                        {manualEntryButtonText}
                      </Button>
                    </MuiBox>
                  </MuiBox>
                )}
                <MuiBox className={classes.totalAmountDivider} />
                <MuiBox className={classes.totalAmountContainer}>
                  <FlexBox direction="row" fullWidth justify="end" align="center">
                    <Typography fontSize={34} fontWeight={600} align="right" margin="0 8px 0 0">
                      {`${t('commonText.total')}${t('commonText.colon')}`}
                    </Typography>
                    <Typography fontSize={34} fontWeight={600} align="right">
                      {formatCurrency(grandTotal, {}, languageSettings)}
                    </Typography>
                  </FlexBox>
                </MuiBox>
              </MuiBox>
            )}
          </MuiBox>
        </Grid>
      </Grid>
      {/* Footer */}
      <Footer
        onPrevClick={() => {
          if (mockTickets.length) setShowGoBackWarning(true);
          else onPrevPageClick();
        }}
        onNextClick={onNextPageClick}
        prevText={backButtonText}
        nextText={nextButtonText}
        isNextDisabled={!mockTickets.length}
        skipNavPrompt={Boolean(showGoBackWarning)}
      >
        {isManualEntryEnabled && (
          <MuiBox className={classes.manualEntryButtonBox}>
            <Button
              onClick={() => setShowManualEntry(true)}
              appearance="white"
              customSize="x-large"
              data-testid="type-code-button"
            >
              {manualEntryButtonText}
            </Button>
          </MuiBox>
        )}
      </Footer>

      {showGoBackWarning && (
        <ActionOverlay
          title={
            <Typography variant="h2" align="center" sx={{ lineHeight: 1 }}>
              {t('commonText.goBackTitle')}
            </Typography>
          }
          description={
            <Typography align="center" fontSize={30}>
              {DEFAULT_TEXT.goBackWarning}
            </Typography>
          }
          height={500}
          cancelText={t('commonText.goBack')}
          confirmText={t('commonText.close')}
          onCancel={onPrevPageClick}
          onConfirm={() => setShowGoBackWarning(false)}
        />
      )}

      {cartError && (
        <ActionOverlay
          title={
            <Typography variant="h2" align="center">
              {t('ticketingRoute.ticketsUnavailable')}
            </Typography>
          }
          confirmText={t('commonText.tryAgain')}
          height={660}
          description={
            <Box width={600}>
              <Typography variant="h4" align="center">
                {t('saleError.additionalAssistance', { support })}
              </Typography>
            </Box>
          }
          onConfirm={resetCartError}
        />
      )}
    </>
  );
};

export default SouvenirTicketView;
